import type { Encounter, Patient, Reference } from '@medplum/fhirtypes'
import { TransactionMutationBuilder } from '@seen/core/bundle-builder'
import { ExtendedObservation } from '@seen/core/models/observation'
import type { ConditionEnv } from '@seen/core/observation-logic/evaluate'
import {
  addNoteCaptures,
  noteCaptureQuery,
  noteCapturesByKey,
  hasDraft,
} from '@seen/core/observation-logic/noteCapture'
import { resourceBindingConditionValue } from '@seen/core/resourceBindingRegistry'
import { useQueryClient } from '@tanstack/react-query'
import type { JSONContent } from '@tiptap/core'
import type { Editor } from '@tiptap/core'
import { useCallback } from 'react'
import { hiddenIds } from '#src/components/rich-text-editor/required/requiredGaps.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export interface NoteCaptureTarget {
  // The submitted note, by reference (`Communication/<id>`): the records are
  // keyed by it and the node they came from.
  noteKey: string
  subject: Reference<Patient>
  encounter?: Reference<Encounter>
}

// What a submitted note held, taken when it was submitted: the editor may
// be gone by the time the note exists.
export interface NoteCaptureSnapshot {
  doc: JSONContent
  hidden: ReadonlySet<string>
  variable: ConditionEnv['variable']
}

export function snapshotNoteCaptures(editor: Editor): NoteCaptureSnapshot {
  const contexts = editor.storage.registryVariable?.contexts ?? {}
  return {
    doc: editor.getJSON(),
    hidden: hiddenIds(editor.state),
    variable: (id) => resourceBindingConditionValue(id, contexts),
  }
}

// Whether the note holds any answer to write.
export function holdsAnswers(snapshot: NoteCaptureSnapshot): boolean {
  let held = false
  const visit = (node: JSONContent): void => {
    if (held) {
      return
    }
    if (hasDraft(node.attrs ?? {}) || node.attrs?.answers) {
      held = true
      return
    }
    node.content?.forEach(visit)
  }
  visit(snapshot.doc)
  return held
}

/* Writes the answers a submitted note held (in editors that defer their
 * records; a note may have several, one per section) as final records for its
 * participant, as signing a visit note does on the server, and retracts the
 * records an earlier submission of the same note made that it no longer
 * holds. Hidden questions are left out. Resolves once written. */
export function useNoteCaptureWriter(): (
  snapshots: readonly NoteCaptureSnapshot[],
  target: NoteCaptureTarget
) => Promise<void> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const practitioner = useCurrentPractitioner()
  return useCallback(
    async (snapshots, target) => {
      const transaction = new TransactionMutationBuilder()
      const keys = new Set<string>()
      const earlier = (
        await medplum.searchResourceClass(
          ExtendedObservation,
          noteCaptureQuery(target.noteKey, target.subject)
        )
      ).map((observation) => observation.toFhirResource())
      const existing = noteCapturesByKey(earlier)
      for (const snapshot of snapshots) {
        const written = addNoteCaptures(
          transaction,
          snapshot.doc,
          snapshot.hidden,
          {
            ...target,
            performer: practitioner.reference,
            status: 'final',
            variable: snapshot.variable,
            existing,
          }
        )
        for (const key of written.keys) {
          keys.add(key)
        }
      }
      for (const [key, observation] of existing) {
        if (!keys.has(key)) {
          transaction.patch(observation, (o) => {
            o.status = 'entered-in-error'
          })
        }
      }
      if (!transaction.bundle.entry?.length) {
        return
      }
      await medplum.executeBatch(transaction.bundle)
      // A batch leaves the client's cached searches as they were, so a
      // refetch within its cache window would still miss the records.
      medplum.invalidateSearches('Observation')
      cacheKeys.invalidateResourceType(queryClient, 'Observation')
    },
    [medplum, practitioner.reference, queryClient]
  )
}
